import { InfoOutlined } from "@mui/icons-material";
import { Alert, Paper, Skeleton, Stack } from "@mui/material";
import AcxEmptyState from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import { observer } from "mobx-react";
import { useMemo } from "react";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../../Stores/LLMClassifierBuilderStore";
import { getLLMClassifierVersionComparison } from "../../utils/llmClassifierVersionComparison";
import { groupVersionComparison } from "../../utils/llmClassifierVersionComparisonGroups";
import { llmClassifierVersionLabel } from "../../utils/llmClassifierVersionDisplay";
import { ChangeGroupCard } from "./ChangeGroupCard";
import { EmptyDiffCard } from "./DiffBlockCard";
import { NoConfigChangesCard } from "./NoConfigChangesCard";
import { VersionComparisonHeader } from "./VersionComparisonHeader";

export const LLMClassifierVersionComparison = observer(function (props: {
    onExpand?: () => void;
    hideHeader?: boolean;
}) {
    const store = useStore(LLMClassifierBuilderStore);
    const isLoading = store.getTaskLoading(
        LLMClassifierBuilderStore.Tasks.LoadLLMClassifierVersions,
    );

    const versions = store.llmClassifierVersions;
    const { before, after } = store.comparisonVersions;

    const comparison = useMemo(
        () => getLLMClassifierVersionComparison(before, after),
        [before, after],
    );

    if (isLoading) {
        return (
            <Stack gap={2}>
                <Skeleton height={56} />
                <Skeleton height={280} />
            </Stack>
        );
    }

    if (versions.length < 2 || !before || !after || !comparison) {
        return (
            <AcxEmptyState
                size="small"
                variant="empty"
                overrideTitle="Nothing to compare yet"
                overrideSubTitle="Save another version to see what changed between them."
            />
        );
    }

    const isSameVersion = before.id === after.id;
    const groups = comparison.hasChanges
        ? groupVersionComparison(comparison)
        : [];
    const hasIncompleteSettingsComparison =
        comparison.missingSettingsSides.length > 0;
    const beforeLabel = llmClassifierVersionLabel(before.createdOn) ?? "Before";
    const afterLabel = llmClassifierVersionLabel(after.createdOn) ?? "After";

    return (
        <Stack gap={2}>
            {!props.hideHeader && (
                <Paper
                    variant="outlined"
                    sx={{ borderRadius: 2, px: 2, py: 1.5 }}
                >
                    <VersionComparisonHeader
                        versions={versions}
                        beforeVersion={before}
                        afterVersion={after}
                        onChange={(side, versionId) =>
                            store.setComparisonVersion(side, versionId)
                        }
                        onExpand={props.onExpand}
                    />
                </Paper>
            )}

            {isSameVersion && (
                <EmptyDiffCard
                    icon={
                        <InfoOutlined
                            fontSize="small"
                            sx={{ color: "gray.600" }}
                        />
                    }
                    title="Comparing a version to itself"
                    message="Pick two different versions to see what changed."
                />
            )}

            {!isSameVersion && (
                <>
                    {comparison.isBuilderTypeChange && (
                        <Alert severity="info">
                            These versions use different builders, so their
                            prompts are listed rather than compared line by
                            line.
                        </Alert>
                    )}

                    {comparison.missingSettingsSides.length > 0 && (
                        <Alert severity="info">
                            {missingSettingsMessage(
                                comparison.missingSettingsSides,
                                beforeLabel,
                                afterLabel,
                            )}
                        </Alert>
                    )}

                    {!comparison.hasChanges &&
                        hasIncompleteSettingsComparison && (
                            <EmptyDiffCard
                                icon={
                                    <InfoOutlined
                                        fontSize="small"
                                        sx={{ color: "gray.600" }}
                                    />
                                }
                                title="No comparable changes found"
                                message="The fields recorded by both versions are identical. Some settings could not be compared."
                            />
                        )}

                    {!comparison.hasChanges &&
                        !hasIncompleteSettingsComparison && (
                            <NoConfigChangesCard
                                beforeVersion={before}
                                afterVersion={after}
                                beforeLabel={beforeLabel}
                                afterLabel={afterLabel}
                            />
                        )}

                    {comparison.hasChanges &&
                        groups.map((group) => (
                            <ChangeGroupCard
                                key={group.key}
                                group={group}
                                beforeLabel={beforeLabel}
                                afterLabel={afterLabel}
                            />
                        ))}
                </>
            )}
        </Stack>
    );
});

/** Older versions never recorded model details, visibility, outputs or volume control -- nothing to compare, not "no changes". */
function missingSettingsMessage(
    sides: ("before" | "after")[],
    beforeLabel: string,
    afterLabel: string,
): string {
    const subject =
        sides.length === 2
            ? "Neither version recorded"
            : `${
                  sides[0] === "before" ? beforeLabel : afterLabel
              } predates recording`;

    return `${subject} model details, visibility, outputs and volume control, so those are not compared here.`;
}
